import React, { useLayoutEffect, useRef, useState } from 'react';
import Message from './Message';
import './MessageList.css';

function MessageList({ messages, onLoadMore, hasMore, isLoadingMore }) {
  const bottomRef = useRef(null);
  const listRef = useRef(null);
  const [isAtTop, setIsAtTop] = useState(false);

  // Track whether the user is currently near the bottom. We keep this in a ref
  // so message updates don't cause us to auto-scroll when the user has scrolled up.
  const isNearBottomRef = useRef(true);
  const [showScrollToEnd, setShowScrollToEnd] = useState(false);

  // When we request older history, the server prepends messages. Preserve the
  // user's viewport by adjusting scrollTop based on the scrollHeight delta.
  const pendingHistoryViewportAdjustmentRef = useRef(null);

  useLayoutEffect(() => {
    const listEl = listRef.current;
    if (!listEl) return;

    // If we just prepended history, keep the visible content anchored.
    if (pendingHistoryViewportAdjustmentRef.current && !isLoadingMore) {
      const { prevScrollHeight, prevScrollTop } = pendingHistoryViewportAdjustmentRef.current;
      const newScrollHeight = listEl.scrollHeight;
      const delta = newScrollHeight - prevScrollHeight;
      listEl.scrollTop = prevScrollTop + delta;
      pendingHistoryViewportAdjustmentRef.current = null;
      return;
    }

    // Otherwise, only auto-scroll when the user is already near the bottom.
    if (bottomRef.current && isNearBottomRef.current && !isLoadingMore) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isLoadingMore]);

  const computeIsNearBottom = (el) => {
    // How far the user is from the bottom of the scroll container.
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    return distanceFromBottom <= 120;
  };

  const handleScroll = (e) => {
    const target = e.target;
    const atTop = target.scrollTop === 0;
    setIsAtTop(atTop);

    const nearBottom = computeIsNearBottom(target);
    isNearBottomRef.current = nearBottom;
    setShowScrollToEnd(!nearBottom);

    if (atTop && hasMore && typeof onLoadMore === 'function' && !isLoadingMore) {
      // Capture scroll metrics BEFORE the history is prepended, so we can
      // restore the viewport after the render.
      if (listRef.current) {
        pendingHistoryViewportAdjustmentRef.current = {
          prevScrollHeight: listRef.current.scrollHeight,
          prevScrollTop: listRef.current.scrollTop,
        };
      }
      onLoadMore();
    }
  };

  const scrollToEnd = () => {
    // Treat this as the user intenting to follow the conversation.
    isNearBottomRef.current = true;
    setShowScrollToEnd(false);
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="message-list-wrapper">
      <div
        className="message-list"
        ref={listRef}
        onScroll={handleScroll}
      >
        {hasMore && (
          <div className="history-loader">
            {isLoadingMore ? 'Loading more history…' : (isAtTop ? 'Release to load more history' : 'Scroll up to load more history')}
          </div>
        )}
        {messages.length === 0 ? (
          <div className="empty-state">
            <p>Start a conversation by asking about your health, medications, or care plan.</p>
          </div>
        ) : (
          <>
            {messages.map((message) => (
              <Message key={message.id} message={message} />
            ))}
            <div ref={bottomRef} />
          </>
        )}
      </div>

      {showScrollToEnd && messages.length > 0 && (
        <button
          type="button"
          className="scroll-to-end"
          onClick={scrollToEnd}
          aria-label="Scroll to end"
          title="Scroll to end"
        >
          Scroll to end
        </button>
      )}
    </div>
  );
}

export default MessageList;
